import { useEffect, useState } from "react";
import { getAccessToken } from "./authToken";
import { analyticsTestMode, marketingClientContext } from "./publicAnalytics";

const API_ORIGIN = process.env.REACT_APP_API_URL || "https://api.dopletech.com";

export const EXPERIMENT_SURVEY_INTRO = "survey_intro";
export const EXPERIMENT_VERIFY_EMAIL = "verify_email";
export const EXPERIMENT_LANDING_HERO = "landing_hero";
export const EXPERIMENT_OPTIONS_VALUE_BLOCK = "options_value_block";
export const CONTROL_VARIANT = "control";

export function variantFromAssignment(payload) {
  if (!payload || payload.enabled !== true) return CONTROL_VARIANT;
  const variant = String(payload.variant || "").trim();
  return variant || CONTROL_VARIANT;
}

const controlAssignment = () => ({ enabled: false, variant: CONTROL_VARIANT });

const postAssignment = async (experimentKey, { expose = false } = {}) => {
  if (typeof window === "undefined" || !experimentKey) return null;
  try {
    if (analyticsTestMode()) return controlAssignment();
  } catch {
    // A failed test-mode check falls through to the assignment request.
  }
  const context = marketingClientContext();
  if (!context.anonymous_id) return null;
  const headers = { "Content-Type": "application/json" };
  const accessToken = getAccessToken();
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const response = await fetch(
    `${API_ORIGIN}/api/analytics/experiments/${encodeURIComponent(experimentKey)}/assignment`,
    {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers,
      body: JSON.stringify({
        anonymous_id: context.anonymous_id,
        session_id: context.session_id,
        attribution: context.attribution,
        expose,
      }),
    }
  );
  if (!response.ok) return null;
  return response.json();
};

export async function loadExperimentAssignment(experimentKey) {
  try {
    return await postAssignment(experimentKey, { expose: false });
  } catch {
    return null;
  }
}

export async function recordExperimentExposure(experimentKey) {
  try {
    return await postAssignment(experimentKey, { expose: true });
  } catch {
    return null;
  }
}

// active=false skips the request until the screen exists (verify, after signup).
// The first paint is control, which is also the shipping state: experiments
// start disabled, so visitors do not wait on this request.
export function useFunnelExperiment(experimentKey, { active = true, detail = false } = {}) {
  const [state, setState] = useState({ variant: CONTROL_VARIANT, enabled: false });
  useEffect(() => {
    if (!active) return undefined;
    try {
      if (analyticsTestMode()) return undefined;
    } catch {
      // A failed test-mode check falls through to the assignment request.
    }
    let cancelled = false;
    loadExperimentAssignment(experimentKey).then((result) => {
      if (cancelled) return;
      const enabled = Boolean(result && result.enabled === true);
      setState({ variant: variantFromAssignment(result), enabled });
      if (enabled) {
        recordExperimentExposure(experimentKey);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [experimentKey, active]);
  return detail ? state : state.variant;
}
